import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, useWindowDimensions, View } from "react-native";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppToast from "@toothfairy/shared-ui/AppToast";
import Notification from "../../Components/Notification";
import PageTitle from "../../Components/PageTitle";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairyOutputModal from "../../Components/ToothFairyComponents/ToothFairyOutputModal";
import ToothFairySupportButton from "../../Components/ToothFairyComponents/ToothFairySupportButton";
import ToothFairyClickableOption from "../../Components/ToothFairyComponents/ToothFairyClickableOption";
import ToothFairyResponsiveOptionsModal from "../../Components/ToothFairyComponents/ToothFairyResponsiveOptionsModal";
import useSupport from "../../Hooks/useSupport";
import useWorkspaces from "../../Hooks/useWorkspaces";
import DateFilter from "../Admin/SubComponents/Leafs/DateFilter";
import Styles from "../Styles/Styles";
import TrainingConfigSelection from "./Components/TrainingConfigSelection";
import {
  isLargeScreen,
  isVeryLargeScreen,
  minAppWidth,
} from "../MainViewContainer";
import FineTuningConfigSelection from "./Components/FineTuningConfigSelection";
import FineTunedModelModal from "./Components/FineTunedModelModal";
import Constants from "../../Constants";

dayjs.extend(relativeTime);

const TrainingScreen = ({
  logStatusColors,
  trainingInProgress,
  trainingLogs,
  hasAnyData,
  handleDeleteTrainingLog = () => {},
  handleTrainingCancellation = () => {},
  handleFieldUpdate = () => {},
  onFilterChange = () => {},
  handleDataDownload = () => {},
  markTrainingLogAsActive = () => {},
  handleTrainingInitiation = () => {},
  handleFineTuningStart = () => {},
  handleFineTuningSetupOpening = () => {},
  statusFilter,
  onUpdateStartDate,
  onUpdateEndDate,
  startDate,
  endDate,
  selectedType,
  setSelectedType,
  typeFilter,
  setTypeFilter,
  trainingTypes,
  isSwapingInProgress,
  trainingConfig,
  setTrainingConfig,
  isSwapRequested,
  setSelectedTopics,
  selectedTopics,
  isModalOpen,
  setIsModalOpen,
  isTrainingInitiationModalOpen,
  setIsTrainingInitiationModalOpen,
  isTrainingFineTuningModalOpen,
  setIsTrainingFineTuningModalOpen,
  isModalOptionsOpen,
  setIsModalOptionsOpen,
  manageModelItem,
  setManageModelItem,
  isFineTunedModelModalOpen,
  setIsFineTunedModelModalOpen,
  workspaceToken,
  handleManageModel = () => {},
  ...props
}) => {
  const { url } = useSupport("NLP_TRAINING_PRF");
  const dimensions = useWindowDimensions();
  const AppToasty = AppToast.useToast();
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();

  const typeShortBy = useMemo(() => {
    const m = {};
    (trainingTypes || Constants.TRAINING_TYPES)?.forEach((tt) => {
      m[tt.key] = tt.short;
    });
    return m;
  }, [trainingTypes]);

  const columns = useMemo(
    () => [
      {
        title: t("training.columns.id"),
        width: 0.2,
      },
      {
        title: t("training.columns.name"),
        width: 0.2,
      },
      {
        title: t("training.columns.type", { defaultValue: "Type" }),
        width: 0.1,
      },
      {
        title: t("training.columns.createdBy"),
        width: 0.2,
      },
      {
        title: t("training.columns.lastChanged"),
        width: 0.2,
      },
      {
        title: t("training.columns.duration"),
        width: 0.1,
      },
      {
        title: t("training.columns.status"),
        width: 0.1,
      },
      {
        title: t("training.columns.actions"),
        isAction: true,
        width: 0.00001,
      },
    ],
    [t]
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const styles = styleGenerator(currentTheme);
  const { state } = AppStateManager.useAppStateManager();
  const { isTrainingLogIdActive, isTrainingEnabled, getPlanConfig } =
    useWorkspaces();
  const getUserName = (id) => {
    return state?.workspaceUsers
      ? state?.workspaceUsers[
          state?.workspaceUsers.map((e) => e?.id).indexOf(id)
        ]?.username
      : "";
  };
  const handleItemSelection = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };
  const generateColorForItem = (item) => {
    if (item?.status === "swapping") return currentTheme?.theme?.orange;
    else if (isTrainingLogIdActive(item?.id))
      return currentTheme?.theme?.primary;
    else if (item.status !== "dispatched" && item.status !== "inProgress")
      return currentTheme?.theme?.grey;
    else if (item?.trainingConfig?.datasetOnly)
      return currentTheme?.theme?.grey;
    else return currentTheme?.theme?.red;
  };
  const trainingInitiationModal = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                maxWidth: 600,
                height: "fit-content",
                maxHeight: "90%",
                margin: "auto",
                marginHorizontal: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={isTrainingInitiationModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("training.modals.fineTuningTitle", {
          type: Constants.mapKeysToNames(selectedType),
        })}
        modalInnerContentStyle={{
          paddingLeft: 70,
          paddingRight: 70,
          paddingVertical: 10,
        }}
        closeModal={() => setIsTrainingInitiationModalOpen(false)}
      >
        <TrainingConfigSelection
          handleTrainingInitiation={handleTrainingInitiation}
          handleFineTuningSetupOpening={handleFineTuningStart}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          trainingTypes={trainingTypes}
          setIsTrainingInitiationModalOpen={setIsTrainingInitiationModalOpen}
          setTrainingConfig={setTrainingConfig}
          trainingConfig={trainingConfig}
          setSelectedTopics={setSelectedTopics}
          selectedTopics={selectedTopics}
        />
      </ToothFairyModal>
    );
  }, [
    handleTrainingInitiation,
    selectedType,
    state.activeWorkspaceID,
    isTrainingInitiationModalOpen,
    setIsTrainingInitiationModalOpen,
    setSelectedTopics,
    JSON.stringify(selectedTopics),
    handleFineTuningSetupOpening,
    getPlanConfig(),
  ]);

  const trainingFineTuningStart = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                maxWidth: 600,
                maxHeight: "90%",
                height: "fit-content",
                margin: "auto",
                marginHorizontal: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={isTrainingFineTuningModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("training.modals.fineTuningConfiguration")}
        modalInnerContentStyle={{
          paddingHorizontal: 50,
          paddingVertical: 20,
        }}
        closeModal={() => setIsTrainingFineTuningModalOpen(false)}
      >
        <FineTuningConfigSelection
          setTrainingConfig={setTrainingConfig}
          trainingConfig={trainingConfig}
          handleFineTuningStart={handleFineTuningStart}
          trainingLogId={selectedItem?.id}
        />
      </ToothFairyModal>
    );
  }, [
    JSON.stringify(trainingConfig),
    setTrainingConfig,
    isTrainingFineTuningModalOpen,
    selectedItem?.id,
    getPlanConfig(),
  ]);

  const _actionsChoice = () => {
    return (
      <ToothFairyResponsiveOptionsModal
        isModalOpen={isModalOptionsOpen}
        setIsModalOpen={setIsModalOptionsOpen}
        modalTitle={t("common.actions")}
        scrollViewStyle={{
          height: 250,
        }}
        height={"auto"}
      >
        <ToothFairyClickableOption
          leftIcon={AppAssets.icons.trainAI}
          onPress={() => {
            setIsTrainingInitiationModalOpen(true);
            setIsModalOptionsOpen(false);
          }}
          isClickable={
            !(
              trainingInProgress ||
              !state?.isWorkspaceReady ||
              isSwapingInProgress
            )
          }
        >
          {t("training.startTuning")}
        </ToothFairyClickableOption>
      </ToothFairyResponsiveOptionsModal>
    );
  };

  const isEmpty = !hasAnyData;

  const trainingLogsList = () => {
    return (
      <View
        style={{
          height: dimensions.height - 85,
          minHeight: 200,
          maxWidth: !isLargeScreen() && dimensions.width - 30,
        }}
      >
        {hasAnyData ? (
          <View
            style={{
              height: dimensions.height - 80,
              minHeight: 400,
              zIndex: -1,
            }}
          >
            <AppTable
              showPagination
              itemsPerPage={25}
              marginRightBeforeActions={30}
              actionButtons
              hideSearch={false}
              searchPlaceholder={t("training.searchPlaceholder", {
                defaultValue: "Search by name, status or date...",
              })}
              showDropDownMenuForActions
              mainActionLabel={t("common.view")}
              onClickMainAction={(item, index) => {
                const originalLog = trainingLogs?.find(
                  (log) => log?.id === item?.item?.id
                );
                handleItemSelection(originalLog || item?.item);
              }}
              onActionItemPress={(id, item, index) => {
                const originalLog = trainingLogs?.find(
                  (log) => log?.id === item?.id
                );
                if (id === "EDIT") {
                  handleItemSelection(originalLog || item);
                } else if (id === "MANAGE_MODEL") {
                  handleManageModel(originalLog || item);
                } else if (id === "DELETE") {
                  const actualStatus = originalLog?.status;
                  if (actualStatus === "dispatched")
                    handleDeleteTrainingLog(item?.id);
                  else if (actualStatus === "inProgress")
                    handleTrainingCancellation(item?.id);
                }
              }}
              actions={[
                {
                  label: t("common.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("training.fineTunedModel.manage", "Manage model"),
                  id: "MANAGE_MODEL",
                  icon: AppAssets.icons.hosting,
                },
                {
                  label: t("common.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              isItemActionable={(item) => {
                const _item = item?.item || item;
                const originalLog = trainingLogs?.find(
                  (log) => log?.id === _item?.id
                );
                const actualStatus = originalLog?.status;
                return (
                  actualStatus === "dispatched" ||
                  actualStatus === "inProgress" ||
                  actualStatus === "completed" ||
                  actualStatus === "inError" ||
                  actualStatus === "datasetReady"
                );
              }}
              style={{
                zIndex: -1,
                height: dimensions?.height - 283,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                marginTop: 10,
              }}
              headers={columns}
              items={trainingLogs
                ?.sort((a, b) =>
                  a?._lastChangedAt < b?._lastChangedAt ? 1 : -1
                )
                ?.map((u) => {
                  return {
                    id: u?.id || "",
                    name: u?.name || "No name",
                    type: typeShortBy[u?.type] || u?.type || " ",
                    createdBy: getUserName(u?.createdBy) || " ",
                    lastChanged:
                      dayjs(u._lastChangedAt).format("DD/MM/YYYY") || " ",
                    duration:
                      parseFloat(u?.duration / 1000 || 0)
                        .toFixed(0)
                        .toString() || "0",
                    status: u?.status ? t(`training.status.${u.status}`) : " ",
                    actions: " ",
                  };
                })}
            >
              <DateFilter
                startDate={startDate}
                endDate={endDate}
                onUpdateEndDate={onUpdateEndDate}
                onUpdateStartDate={onUpdateStartDate}
                paddingBottom={0}
              />
            </AppTable>
          </View>
        ) : (
          <View
            style={{
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              marginVertical: 20,
            }}
          >
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "auto",
                marginHorizontal: "auto",
                justifyContent: "center",
              }}
              size={30}
              icon={AppAssets.icons.orbit}
              mainCaption={t("training.emptyPlaceholder")}
              subCaption={t("training.emptyPlaceholderSub")}
            />
            <ToothFairyButton
              {...Styles.minifiedButtonStyle}
              wrapperStyle={{ marginTop: 24 }}
              style={{
                marginRight: 0,
                height: Constants.appButtonHeight,
                width: 120,
                marginLeft: 0,
              }}
              isDisabled={
                trainingInProgress ||
                !state?.isWorkspaceReady ||
                isSwapingInProgress
              }
              onPress={() => {
                setIsTrainingInitiationModalOpen(true);
              }}
              title={t("training.startTuning")}
              accessibilityLabel="trainingButton"
            />
          </View>
        )}
      </View>
    );
  };

  return (
    <AppBaseScreen
      isScrollable={true}
      contentContainerStyle={{
        overflow: "auto",
      }}
      style={{ minHeight: 700 }}
    >
      <View style={styles.container}>
        <View style={styles.titleContainer}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
            }}
          >
            <PageTitle>{t("training.title")}</PageTitle>
            <ToothFairySupportButton />
            {!isEmpty &&
              (isLargeScreen() ? (
                <>
                  {" "}
                  <ToothFairyButton
                    {...Styles.minifiedButtonStyle}
                    isDisabled={
                      trainingInProgress ||
                      !state?.isWorkspaceReady ||
                      isSwapingInProgress
                    }
                    onPress={() => {
                      setIsTrainingInitiationModalOpen(true);
                    }}
                    title={t("training.startTuning")}
                    accessibilityLabel="trainingButton"
                  />
                </>
              ) : (
                <ToothFairyButton
                  {...Styles.minifiedButtonStyle}
                  onPress={() => {
                    setIsModalOptionsOpen(true);
                  }}
                  title={t("common.actions")}
                  accessibilityLabel="actionsButton"
                />
              ))}
          </View>

          <View>
            {trainingInProgress && (
              <Notification
                style={{
                  right: 0,
                  // top: 10,
                }}
                color={"primary"}
                dotValue={1}
                label={!isVeryLargeScreen() ? " " : t("training.inProgress")}
                isProgress={true}
              />
            )}
            {isSwapingInProgress ? (
              <Notification
                style={{
                  right: 0,
                }}
                color={"primary"}
                dotValue={1}
                label={
                  !isVeryLargeScreen() ? " " : t("training.swapInProgress")
                }
                isProgress={true}
              />
            ) : (
              <></>
            )}
          </View>
        </View>
        {trainingLogsList()}

        <ToothFairyOutputModal
          isEventDriven={true}
          path={"graph"}
          resultObjectTitle={t("training.modals.trainingOutput")}
          responseCopyBtnTitle={t("training.modals.copyOutput")}
          resultObjectIcon={AppAssets.icons.trainAI}
          createdBy={selectedItem?.createdBy}
          requestPayload={{
            input: {
              createdBy: selectedItem?.createdBy,
              owner: selectedItem?.owner,
              trainingConfig: selectedItem?.trainingConfig,
              status: "dispatched",
              workspaceID: selectedItem?.workspaceID,
              type: selectedItem?.type,
            },
          }}
          showSummary={true}
          handleFieldUpdate={(id, property, value) => {
            handleFieldUpdate(id, property, value);
            setSelectedItem({
              ...selectedItem,
              [property]: value,
            });
          }}
          name={selectedItem?.name}
          description={selectedItem?.description}
          includeConfig={true}
          configObjectTitle={t("training.modals.trainingConfig")}
          configObjectIcon={AppAssets.icons.cogWheel}
          configObject={selectedItem?.trainingConfig}
          isOutputUpToDate={true}
          handleOutputResize={() => {}}
          isOutputExpanded={true}
          trainingInProgress={trainingInProgress}
          isSwapingInProgress={isSwapingInProgress}
          isSwapRequested={isSwapRequested}
          parsingInProgress={false}
          trainingLogId={selectedItem?.id}
          data={selectedItem?.trainingMeta}
          meta={selectedItem?.trainingMeta}
          subCaption={t("training.modals.trainerMetaSubCaption")}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          checkLength={false}
          handleDataDownload={handleDataDownload}
          markTrainingLogAsActive={markTrainingLogAsActive}
          datasetOnly={selectedItem?.trainingConfig?.datasetOnly}
          trainingLogData={selectedItem}
          lastChangedAt={dayjs(selectedItem?._lastChangedAt).format(
            "DD/MM/YYYY"
          )}
          handleTrainingInitiation={handleTrainingInitiation}
          handleFineTuningStart={handleFineTuningStart}
          handleFineTuningSetupOpening={handleFineTuningSetupOpening}
        />
      </View>
      {trainingInitiationModal}
      {trainingFineTuningStart}
      {_actionsChoice()}
      <FineTunedModelModal
        trainingLog={manageModelItem}
        token={workspaceToken}
        handleDataDownload={handleDataDownload}
        isOpen={isFineTunedModelModalOpen}
        setIsOpen={setIsFineTunedModelModalOpen}
      />
    </AppBaseScreen>
  );
};

const styleGenerator = (currentTheme) => {
  return StyleSheet.create({
    container: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      minWidth: minAppWidth(),
    },
    listContentContainer: {
      padding: 0,
    },
    listHeader: {
      justifyContent: "space-around",
      // backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
      justifyContent: "space-around",
    },
    paginationWrapper: {
      marginTop: 10,
      alignSelf: "flex-end",
    },
    title: {
      fontSize: currentTheme?.theme?.largest_font_size,
      fontWeight: currentTheme?.theme?.font_bold,
      marginRight: 20,
    },
    titleContainer: {
      alignItems: "center",
      flexDirection: "row",
      justifyContent: "space-between",
    },
  });
};

export default TrainingScreen;
